import {useCallback, useState} from 'react'
import type {
  NormalizedPrompt,
  AskUserArguments,
  ElicitationAskUserArguments,
  ExitPlanModeArguments,
  PermissionRequestArguments,
  SimpleAskUserArguments,
} from '@github-ui/copilot-shared-ui/ahp/interactive-prompt-types'
import type {CommandType} from '@github-ui/copilot-shared-ui/ahp/command-types'
import {isElicitationAskUser} from '@github-ui/copilot-shared-ui/ahp/interactive-prompt-types'
import {COMMAND_TYPES} from '@github-ui/copilot-shared-ui/ahp/command-types'
import {SimpleAskUserDisplay} from '../session-logs/SimpleAskUserDisplay'
import {ElicitationAskUserDisplay} from '../session-logs/ElicitationAskUserDisplay'
import {PermissionDisplay} from '../session-logs/PermissionDisplay'
import {PlanApprovalDisplay} from '../session-logs/PlanApprovalDisplay'
import {formatMcpArgsForPreview} from '../../utils/permission-prompt-helpers'
import {PermissionDiffPreview} from '../session-logs/PermissionDiffPreview'

interface PromptInputProps {
  /** The pending prompt to render */
  prompt: NormalizedPrompt
  /** Navigation metadata for sessions with multiple unresolved prompts */
  promptNavigation?: PromptNavigation
  /** Callback when user responds to the prompt, including the command type for the response */
  onRespond?: (toolCallId: string, response: unknown, commandType: CommandType) => void
  /** Callback to dismiss/skip the prompt */
  onDismiss?: () => void
  /** When true, disables all interactive controls (e.g. while a previous response is in flight) */
  disabled?: boolean
}

export interface PromptNavigation {
  current: number
  total: number
  onPrevious: () => void
  onNext: () => void
}

/**
 * Renders a CLI prompt (ask_user, permission_request, exit_plan_mode) in the chat input area.
 * Thin router that delegates to display components; each uses UserChoiceCard for its own chrome.
 */
export function PromptInput({prompt, promptNavigation, onRespond, onDismiss, disabled = false}: PromptInputProps) {
  const [respondedToolCallId, setRespondedToolCallId] = useState<string | null>(null)
  const {toolName, toolCallId, arguments: args} = prompt

  if (respondedToolCallId === toolCallId && !disabled) {
    setRespondedToolCallId(null)
  }

  const isDisabled = respondedToolCallId === toolCallId || disabled

  const handleRespond = useCallback(
    (id: string, response: unknown, commandType: CommandType) => {
      if (isDisabled) return
      setRespondedToolCallId(id)
      onRespond?.(id, response, commandType)
    },
    [isDisabled, onRespond],
  )

  const handleDismiss = useCallback(() => {
    if (isDisabled) return
    setRespondedToolCallId(toolCallId)
    onDismiss?.()
  }, [isDisabled, onDismiss, toolCallId])

  switch (toolName) {
    case 'ask_user':
      return (
        <AskUserPromptContent
          args={args as AskUserArguments}
          toolCallId={toolCallId}
          onRespond={handleRespond}
          onDismiss={handleDismiss}
          disabled={isDisabled}
        />
      )
    case 'permission_request':
      return (
        <PermissionPromptContent
          args={args as PermissionRequestArguments}
          toolCallId={toolCallId}
          promptNavigation={promptNavigation}
          onRespond={handleRespond}
          disabled={isDisabled}
        />
      )
    case 'exit_plan_mode':
      return (
        <PlanApprovalPromptContent
          args={args as ExitPlanModeArguments}
          toolCallId={toolCallId}
          onRespond={handleRespond}
          disabled={isDisabled}
        />
      )
    default:
      return null
  }
}

interface AskUserPromptContentProps {
  args: AskUserArguments
  toolCallId: string
  onRespond?: (toolCallId: string, response: unknown, commandType: CommandType) => void
  onDismiss?: () => void
  disabled?: boolean
}

function AskUserPromptContent({args, toolCallId, onRespond, onDismiss, disabled}: AskUserPromptContentProps) {
  if (isElicitationAskUser(args)) {
    return (
      <ElicitationAskUserPromptContent args={args} toolCallId={toolCallId} onRespond={onRespond} disabled={disabled} />
    )
  }

  return (
    <SimpleAskUserPromptContent
      args={args}
      toolCallId={toolCallId}
      onRespond={onRespond}
      onDismiss={onDismiss}
      disabled={disabled}
    />
  )
}

interface SimpleAskUserPromptContentProps {
  args: SimpleAskUserArguments
  toolCallId: string
  onRespond?: (toolCallId: string, response: unknown, commandType: CommandType) => void
  onDismiss?: () => void
  disabled?: boolean
}

function SimpleAskUserPromptContent({
  args,
  toolCallId,
  onRespond,
  onDismiss,
  disabled,
}: SimpleAskUserPromptContentProps) {
  const handleChoose = useCallback(
    (choice: string) => {
      onRespond?.(
        toolCallId,
        {promptId: toolCallId, answer: choice, wasFreeform: false},
        COMMAND_TYPES.ASK_USER_RESPONSE,
      )
    },
    [toolCallId, onRespond],
  )

  const handleFreeform = useCallback(
    (text: string) => {
      onRespond?.(toolCallId, {promptId: toolCallId, answer: text, wasFreeform: true}, COMMAND_TYPES.ASK_USER_RESPONSE)
    },
    [toolCallId, onRespond],
  )

  const handleSkip = useCallback(() => {
    onRespond?.(
      toolCallId,
      {promptId: toolCallId, answer: '', wasFreeform: false, dismissed: true},
      COMMAND_TYPES.ASK_USER_RESPONSE,
    )
    onDismiss?.()
  }, [toolCallId, onRespond, onDismiss])

  return (
    <SimpleAskUserDisplay
      question={args.question}
      choices={'choices' in args && args.choices ? args.choices : []}
      multiSelect={false}
      allowFreeform={args.allowFreeform ?? true}
      onChoose={handleChoose}
      onFreeform={handleFreeform}
      onSkip={handleSkip}
      disabled={disabled ?? false}
    />
  )
}

interface ElicitationAskUserPromptContentProps {
  args: ElicitationAskUserArguments
  toolCallId: string
  onRespond?: (toolCallId: string, response: unknown, commandType: CommandType) => void
  disabled?: boolean
}

function ElicitationAskUserPromptContent({
  args,
  toolCallId,
  onRespond,
  disabled,
}: ElicitationAskUserPromptContentProps) {
  const handleElicitationSubmit = useCallback(
    (content: Record<string, string | string[]>) => {
      onRespond?.(toolCallId, {promptId: toolCallId, action: 'accept', content}, COMMAND_TYPES.ELICITATION_RESPONSE)
    },
    [toolCallId, onRespond],
  )

  const handleDecline = useCallback(() => {
    onRespond?.(toolCallId, {promptId: toolCallId, action: 'decline'}, COMMAND_TYPES.ELICITATION_RESPONSE)
  }, [toolCallId, onRespond])

  return (
    <ElicitationAskUserDisplay
      message={args.message}
      schema={args.requestedSchema}
      onElicitationSubmit={handleElicitationSubmit}
      onDecline={handleDecline}
      disabled={disabled ?? false}
    />
  )
}

interface PermissionPromptContentProps {
  args: PermissionRequestArguments
  toolCallId: string
  promptNavigation?: PromptNavigation
  onRespond?: (toolCallId: string, response: unknown, commandType: CommandType) => void
  disabled?: boolean
}

function PermissionPromptContent({
  args,
  toolCallId,
  promptNavigation,
  onRespond,
  disabled,
}: PermissionPromptContentProps) {
  const {
    kind,
    toolName,
    serverName,
    description,
    command,
    filePath,
    diff,
    args: mcpArgs,
    canOfferSessionApproval,
    commandIdentifiers,
    toolArgs,
  } = args

  const toolDisplayName = description?.trim() || toolName

  const handleRespond = useCallback(
    (approved: boolean, scope?: 'once' | 'session') => {
      onRespond?.(
        toolCallId,
        {promptId: toolCallId, approved, scope: scope ?? 'once', toolDisplayName},
        COMMAND_TYPES.PERMISSION_RESPONSE,
      )
    },
    [toolCallId, onRespond, toolDisplayName],
  )

  let detail = command || filePath || formatMcpArgsForPreview(mcpArgs)
  if (!detail && kind === 'hook' && toolArgs) {
    detail = `Tool: ${toolName ?? kind ?? 'unknown'}\nArgs: ${toolArgs}`
  }

  return (
    <PermissionDisplay
      kind={kind}
      toolName={toolName}
      serverName={serverName}
      intention={description}
      detail={diff ? undefined : detail}
      previewContent={diff ? <PermissionDiffPreview diff={diff} filePath={filePath} /> : undefined}
      canOfferSessionApproval={canOfferSessionApproval}
      commandIdentifiers={commandIdentifiers}
      promptNavigation={promptNavigation}
      onRespond={handleRespond}
      disabled={disabled}
    />
  )
}

interface PlanApprovalPromptContentProps {
  args: ExitPlanModeArguments
  toolCallId: string
  onRespond?: (toolCallId: string, response: unknown, commandType: CommandType) => void
  disabled?: boolean
}

function PlanApprovalPromptContent({args, toolCallId, onRespond, disabled}: PlanApprovalPromptContentProps) {
  const {summary, planContent, actions = [], recommendedAction} = args

  const handleAction = useCallback(
    (action: string) => {
      onRespond?.(
        toolCallId,
        {promptId: toolCallId, approved: true, selectedAction: action},
        COMMAND_TYPES.PLAN_APPROVAL_RESPONSE,
      )
    },
    [toolCallId, onRespond],
  )

  const handleFeedback = useCallback(
    (feedback: string) => {
      onRespond?.(toolCallId, {promptId: toolCallId, approved: false, feedback}, COMMAND_TYPES.PLAN_APPROVAL_RESPONSE)
    },
    [toolCallId, onRespond],
  )

  return (
    <PlanApprovalDisplay
      summary={summary}
      planContent={planContent}
      actions={actions}
      recommendedAction={recommendedAction}
      onAction={handleAction}
      onFeedback={handleFeedback}
      disabled={disabled}
      hideModelPicker
    />
  )
}
